// ShiftLogs.js

/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import {
  Box, TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Paper, Grid, FormControl, Button, TableFooter
} from "@mui/material";
import Loader from '../../common/Loader';
import PageTitle from '../../common/PageTitle';
import BasicSelector from '../../common/Selector';
import LocalDateSelector from '../../common/LocalDateSelector';
import EmptyTable from '../../common/EmptyTable';
import { getAPI, postAPI } from '../../network';
import { useParams } from 'react-router-dom';
import {
  checkAuthority,
  getTimeFromTimestamp2,
  tableData,
  tableHeader
} from '../../utils';

export default function ShiftLogs() {
  const { siteId } = useParams();
  const [loader, setLoader] = useState(false);
  const [shiftLogs, setShiftLogs] = useState([]); // All data
  const [filteredShiftLogs, setFilteredShiftLogs] = useState([]);
  const [users, setUsers] = useState([]);

  const [filter, setFilter] = useState({
    userId: '',
    startDate: null,
    endDate: null,
  });

  const [page, setPage] = useState(0);
  const [rowsPerPageOptions] = useState([5, 10, 25, 50]);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const getShiftLogs = async () => {
    setLoader(true);
    try {
      const data = await getAPI(`/shift-logs?siteId=${siteId}`);
      if (data) {
        setShiftLogs(data);
        setFilteredShiftLogs(data);

        // Extract users for filter dropdown
        const uniqueUsers = Array.from(
          new Map(
            data
              .filter(item => item?.shiftId?.assignedUser)
              .map(item => [
                item.shiftId.assignedUser._id,
                {
                  label: `${item.shiftId.assignedUser.firstname} ${item.shiftId.assignedUser.lastname}`,
                  value: item.shiftId.assignedUser._id
                }
              ])
          ).values()
        );
        setUsers(uniqueUsers);
      }
    } catch (error) {
      console.error("Failed to fetch shift logs", error);
    }
    setLoader(false);
  };

  const handleResendEmail = async (shiftLogId) => {
    try {
      setLoader(true);
      await postAPI(`/user/shifts/resend-logs/${shiftLogId}`, { shiftLogId });
    } catch (error) {
      console.error("Resend email failed", error);
    } finally {
      setLoader(false);
    }
  };

  const handleClearFilters = () => {
    setFilter({
      userId: '',
      startDate: null,
      endDate: null,
    });
    setFilteredShiftLogs(shiftLogs);
    setPage(0);
  };

  // Apply filtering
  useEffect(() => {
    let filtered = [...shiftLogs];

    if (filter.userId) {
      filtered = filtered.filter(item =>
        item?.shiftId?.assignedUser?._id === filter.userId
      );
    }
    if (filter.startDate) {
      const start = new Date(filter.startDate).setHours(0, 0, 0, 0);
      filtered = filtered.filter(item =>
        new Date(item.createdAt).getTime() >= start
      );
    }
    if (filter.endDate) {
      const end = new Date(filter.endDate).setHours(23, 59, 59, 999);
      filtered = filtered.filter(item =>
        new Date(item.createdAt).getTime() <= end
      );
    }

    setFilteredShiftLogs(filtered);
    setPage(0);
  }, [filter, shiftLogs]);

  useEffect(() => {
    getShiftLogs();
  }, []);

  return (
    <Box>
      <Loader loader={loader} />
      <PageTitle title="Shift Logs" />
      <Box ml={1}>
        <FormControl fullWidth>
          <Grid container spacing={2} my={2}>
            <Grid item xs={12} md={4} lg={3}>
              <BasicSelector
                disableAll={true}
                options={users}
                value={filter.userId}
                name="Select User"
                onChange={(e) => setFilter(prev => ({ ...prev, userId: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <LocalDateSelector
                title="Start Date"
                value={filter.startDate}
                onChange={(date) => setFilter(prev => ({ ...prev, startDate: date }))}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <LocalDateSelector
                title="End Date"
                value={filter.endDate}
                onChange={(date) => setFilter(prev => ({ ...prev, endDate: date }))}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={3} display="flex" alignItems="center">
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </FormControl>

        {checkAuthority('VIEW_TASKS') && (
          <Box my={3}>
            <TableContainer component={Paper}>
              <Table aria-label="shift logs table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeader}>Date</TableCell>
                    <TableCell sx={tableHeader}>Site</TableCell>
                    <TableCell sx={tableHeader}>Shift Type</TableCell>
                    <TableCell sx={tableHeader}>Resource Name</TableCell>
                    <TableCell sx={tableHeader}>Time Submitted</TableCell>
                    <TableCell sx={tableHeader}>Export</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredShiftLogs
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((log) => (
                      <TableRow key={log._id}>
                        <TableCell sx={tableData}>{log?.createdAt?.split("T")[0]}</TableCell>
                        <TableCell sx={tableData}>{log?.shiftId?.siteId?.name || "-"}</TableCell>
                        <TableCell sx={tableData}>
                          {log?.shiftId?.shiftType === "Fixed" ? "Rostered" : "Casual"}
                        </TableCell>
                        <TableCell sx={tableData}>
                          {`${log?.shiftId?.assignedUser?.firstname} ${log?.shiftId?.assignedUser?.lastname}`}
                        </TableCell>
                        <TableCell sx={tableData}>
                          {getTimeFromTimestamp2(log?.createdAt)}
                        </TableCell>
                        <TableCell sx={tableData}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleResendEmail(log.shiftId._id)}
                          >
                            Resend Email
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  {filteredShiftLogs.length === 0 && (
                    <EmptyTable colSpan={6} message="No shift logs found." />
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={rowsPerPageOptions}
                      count={filteredShiftLogs.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    </Box>
  );
}
